import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { fetchBookRangeByDate } from "../../../redux/reportSummarySlice";
import CardLayout from "./CardLayout";
import { logger } from "../../../utils/logger";

const FinancialYearFilter = () => {
  const [fromdate, setFromdate] = useState(null);
  const [todate, setTodate] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [noRecords, setNoRecords] = useState(false); // track no records
  const dispatch = useDispatch();
  const { bookRange, loading, error } = useSelector(
    (state) => state.summaryReport
  );
  const { user } = useSelector((state) => state.auth);
  const handleFilter = async () => {
    if (!fromdate || !todate) return;

    // Prevent same start and end date
    if (dayjs(fromdate).isSame(dayjs(todate), "day")) {
      setNoRecords(true);
      setShowCard(false);
      return;
    }

    try {
      const response = await dispatch(
        fetchBookRangeByDate({ fromdate, todate })
      );

      if (!response.payload || Object.keys(response.payload).length === 0) {
        setNoRecords(true);
        setShowCard(false);
        logger.info(
          "Financial year Starting/Ending Book no successfully fetched",
          {
            fromDate: fromdate,
            toDate: todate,
            username: user?.username,
            role: user?.role,
          }
        );
      } else {
        setNoRecords(false);
        setShowCard(true);
        logger.warn("No Starting/Ending Book No found for this date range", {
          fromDate: fromdate,
          toDate: todate,
          username: user?.username,
          role: user?.role,
        });
      }
    } catch (error) {
      logger.error("Error occur during Start/End Book No fetch", {
        error: error,
      });
      setNoRecords(true);
      setShowCard(false);
    }
  };

  return (
    <>
      <div>
        <Box 
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            marginBottom: 2,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* From Date */}
            <DatePicker
              label="From Date"
              value={fromdate}
              onChange={(newValue) => setFromdate(newValue)}
              format="DD/MM/YYYY"
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  readOnly: true,
                  size: "small",
                  fullWidth: true,
                },
              }}
              sx={{ maxWidth: 150 }}
            />
            {/* To Date */}
            <DatePicker
              label="To Date"
              value={todate}
              onChange={(newValue) => setTodate(newValue)}
              format="DD/MM/YYYY"
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  readOnly: true,
                  size: "small",
                  fullWidth: true,
                },
              }}
              sx={{ maxWidth: 150 }}
            />
          </LocalizationProvider>
          <Button variant="contained" color="primary" onClick={handleFilter}>
            Apply Filter
          </Button>
        </Box>

        {/* Loader */}
        {loading && (
          <Box display="flex" justifyContent="center" mt={3}>
            <CircularProgress />
          </Box>
        )}

        {/* No records message */}
        {!loading && noRecords && (
          <Box textAlign="center" mt={2} fontWeight="bold" color="black">
            Start date and end date should not be the same. Choose a different
            date range.
          </Box>
        )}

        {/* Display Book & Receipt Number Range */}
        {!loading && showCard && (
          <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
            <CardLayout
              title="Starting Book No"
              value={bookRange?.startBookNo}
            />
            <CardLayout title="Ending Book No" value={bookRange?.endBookNo} />
            <CardLayout
              title="Starting Receipt No"
              value={bookRange?.startReceiptNo}
            />
            <CardLayout
              title="Ending Receipt No"
              value={bookRange?.endReceiptNo}
            />
          </Box>
        )}
      </div>
    </>
  );
};

export default FinancialYearFilter;
